import type { TevmConfig } from "./types.js";

/** Fork preset configuration. */
export interface ForkPreset {
  name: string;
  rpcUrl: string;
  /** Additional RPC URLs to try if the primary fails. */
  fallbackRpcUrls: string[];
  chainId: number;
  description: string;
}

/**
 * CORS-friendly RPC presets that work from browser without API keys.
 *
 * Primary URLs are chosen for reliability with `eth_getProof` (required by
 * Tevm fork transport). Fallbacks are tried in order when the primary fails.
 */
export const FORK_PRESETS: Record<string, ForkPreset> = {
  ethereum: {
    name: "Ethereum Mainnet",
    rpcUrl: "https://eth.drpc.org",
    fallbackRpcUrls: [
      "https://cloudflare-eth.com",
      "https://ethereum-rpc.publicnode.com",
    ],
    chainId: 1,
    description: "Fork Ethereum mainnet (free, no API key)",
  },
  base: {
    name: "Base",
    rpcUrl: "https://mainnet.base.org",
    fallbackRpcUrls: ["https://base-rpc.publicnode.com"],
    chainId: 8453,
    description: "Fork Base L2 (free, no API key)",
  },
};

/** Result of RPC URL validation. */
export interface RpcValidationResult {
  valid: boolean;
  chainId?: number;
  blockNumber?: bigint;
  error?: string;
}

/**
 * Validate an RPC URL by firing eth_chainId and eth_blockNumber.
 * Detects CORS errors and returns actionable error messages.
 */
export async function validateRpcUrl(
  url: string,
): Promise<RpcValidationResult> {
  try {
    new URL(url);
  } catch {
    return { valid: false, error: "Invalid URL format" };
  }

  try {
    const [chainIdRes, blockNumberRes] = await Promise.all([
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          jsonrpc: "2.0",
          id: 1,
          method: "eth_chainId",
          params: [],
        }),
      }),
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          jsonrpc: "2.0",
          id: 2,
          method: "eth_blockNumber",
          params: [],
        }),
      }),
    ]);

    if (!chainIdRes.ok || !blockNumberRes.ok) {
      return {
        valid: false,
        error: `RPC returned HTTP ${String(chainIdRes.status)}`,
      };
    }

    const chainIdData = (await chainIdRes.json()) as {
      result?: string;
      error?: { message: string };
    };
    const blockNumberData = (await blockNumberRes.json()) as {
      result?: string;
      error?: { message: string };
    };

    if (chainIdData.error) {
      return { valid: false, error: `RPC error: ${chainIdData.error.message}` };
    }

    const chainId = Number(chainIdData.result);
    const blockNumber = BigInt(blockNumberData.result ?? "0x0");

    return { valid: true, chainId, blockNumber };
  } catch (err) {
    // Likely a CORS error
    const message = err instanceof Error ? err.message : String(err);
    if (message.includes("Failed to fetch") || message.includes("CORS")) {
      return {
        valid: false,
        error:
          "CORS error: This RPC endpoint does not allow browser requests. Use a CORS-friendly RPC (e.g., Cloudflare ETH, Base Public) or configure CORS on your RPC provider.",
      };
    }
    return { valid: false, error: message };
  }
}

/** Build a TevmConfig from a fork preset name. */
export function forkConfigFromPreset(
  preset: keyof typeof FORK_PRESETS,
  options?: { blockNumber?: bigint; fundAmountEth?: number },
): TevmConfig {
  const p = FORK_PRESETS[preset];
  if (!p) throw new Error(`Unknown fork preset: ${preset}`);
  return {
    mode: preset === "ethereum" ? "fork-ethereum" : "fork-base",
    chainId: p.chainId,
    rpcUrl: p.rpcUrl,
    fallbackRpcUrls: p.fallbackRpcUrls,
    blockNumber: options?.blockNumber,
    fundAmountEth: options?.fundAmountEth,
  };
}

/** Build a TevmConfig from a custom RPC URL. */
export function forkConfigFromUrl(
  rpcUrl: string,
  chainId: number,
  blockNumber?: bigint,
): TevmConfig {
  return {
    mode: "fork-custom",
    chainId,
    rpcUrl,
    blockNumber,
  };
}

/**
 * Translate raw RPC/Tevm error messages into user-friendly text.
 * Used by both the worker and the UI.
 */
export function humanizeRpcError(message: string): string {
  if (
    message.includes("old data not available due to pruning") ||
    message.includes("missing trie node")
  ) {
    return "RPC node has pruned historical state data. This can happen with free RPC endpoints. Try again or use a different provider.";
  }
  if (
    message.includes("rate limit") ||
    message.includes("429") ||
    message.includes("Too Many Requests")
  ) {
    return "RPC rate limit reached. Please wait a moment and try again.";
  }
  if (message.includes("Failed to fetch") || message.includes("CORS")) {
    return "Cannot reach the RPC endpoint. This is usually a CORS or network issue. Try a different RPC provider.";
  }
  if (message.includes("timed out")) {
    return "RPC request timed out. The node may be overloaded — try again or use a different provider.";
  }
  if (
    message.includes("Internal error") ||
    message.includes("internal server error") ||
    message.includes("502") ||
    message.includes("503")
  ) {
    return "The RPC node returned a server error. Try again in a few seconds.";
  }
  if (message.includes("Account setup failed")) {
    return "Failed to set up test accounts on the forked chain. The RPC provider may be unreliable — try again.";
  }
  if (message.includes("Tevm Worker initialization timed out")) {
    return "Fork initialization timed out. The RPC endpoint may be slow — try again or use a different provider.";
  }
  if (message.includes("Tevm Worker failed to initialize")) {
    // Strip the prefix wrapper and humanize the inner message
    const inner = message.replace("Tevm Worker failed to initialize: ", "");
    return humanizeRpcError(inner);
  }
  // Pass through unknown errors but trim excessive detail
  if (message.length > 200) {
    return message.slice(0, 200) + "...";
  }
  return message;
}
